import {
    type Member,
    memberListSchema,
    memberPath,
    memberSchema,
    membersPath,
    type RemovedMember,
    removedMemberSchema,
    type UpdateMemberInput,
} from '@baby-tracker/contracts';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiGet, apiRequest } from '../lib/api-client.js';
import { queryKeys } from '../lib/query-keys.js';

export function useMembers(familyId: string | undefined): UseQueryResult<Member[]> {
    return useQuery({
        queryKey: queryKeys.members(familyId ?? ''),
        enabled: familyId !== undefined,
        queryFn: ({ signal }) => apiGet(membersPath(familyId ?? ''), memberListSchema, { signal }),
    });
}

export function useUpdateMember(
    familyId: string,
): UseMutationResult<Member, Error, { userId: string; body: UpdateMemberInput }> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: { userId: string; body: UpdateMemberInput }) =>
            apiRequest('PATCH', memberPath(familyId, input.userId), memberSchema, {
                body: input.body,
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.members(familyId) });
            await queryClient.invalidateQueries({ queryKey: queryKeys.me });
        },
    });
}

export function useRemoveMember(familyId: string): UseMutationResult<RemovedMember, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (userId: string) =>
            apiRequest('DELETE', memberPath(familyId, userId), removedMemberSchema),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.members(familyId) });
            await queryClient.invalidateQueries({ queryKey: queryKeys.me });
        },
    });
}
